import styled from '@emotion/styled';
import { CheckCircleOutlined, Edit, ExpandMore, InfoOutlined } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    Box,
    Button,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Popover,
    Select,
    Stack,
    Switch,
    TextField,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { toast } from 'sonner';
import carfex from '../../../../src/assets/carfex.png';
import amex from '../../../assets/CardImages/amex.png';
import discover from '../../../assets/CardImages/discover.png';
import mastercard from '../../../assets/CardImages/mastercard.png';
import visa from '../../../assets/CardImages/visa.png';
import MultiTaxRows from '../../../common/Accordians/MultiTaxRows';
import EditRecordPaymentDialog from '../../../common/EditRecordPaymentDialog';
import LinkedAppointmentDialog from '../../../common/LinkedAppointmentDialog';
import { APPOINTMENT_STATUS, APPOINTMENT_STATUS_LABEL } from '../../../config/dataService/constants';
import { MuiMultiFieldWithFocus, MuiSelectWithFocus, MuiTextFieldWithFocus } from '../../../config/MaterialUIConstants';
import { appointmentKeys, useUpdateAppointmentStatus } from '../../../services/business/appointment/appointment.hooks';
import { useGetShopTaxes } from '../../../services/core/settings';
import { DEFAULT_EMAIL_MESSAGES } from '../../../utility/Constant';
import { CustomTooltip, formatNumberAsWhole, HtmlTooltipSecondary } from '../../../utility/helpers/commonFunction';
import { computeLabourPartsFromServices } from '../../../utility/helpers/computeLabourPartsFromServices';
import { NumericEngine } from '../../../utility/helpers/numericEngine';
import {
    buildTaxableLinesFromRecord,
    calculateMultiTaxFromLines,
    calculateMultiTaxLines,
    isNewTaxVersion as checkIsNewTaxVersion,
    DOCUMENT_TAX_TYPE,
    getShopTaxRules,
    mergeShopTaxRulesIntoBreakdown,
    normalizeTaxesBreakdownFromResponse,
    resolveBreakdownForCalc,
    shouldSyncCalculatedBreakdown,
    summarizeTaxRows,
} from '../../../utility/helpers/taxCalculation';
import GoogleLogoSvg from '../../../utility/Svg/GoogleLogoSvg';
import PaymentBreakdownSvg from '../../../utility/Svg/PaymentBreakdownSvg';
import RecordPaymentDetailSvg from '../../../utility/Svg/RecordPaymentDetailSvg';
import ReviewRequestAppSvg from '../../../utility/Svg/ReviewRequestAppSvg';
import ReviewRequestEmailSvg from '../../../utility/Svg/ReviewRequestEmailSvg';
import ReviewRequestTextSvg from '../../../utility/Svg/ReviewRequestTextSvg';
import SaveAndSendSvg from '../../../utility/Svg/SaveAndSendSvg';
/**
 * Payment method options with logos
 */
export const PAYMENT_METHODS = [
    { key: 'credit', label: 'Credit' },
    { key: 'cash', label: 'Cash' },
    { key: 'debit', label: 'Debit' },
    { key: 'e_transfer', label: 'E-Transfer' },
    { key: 'cheque', label: 'Cheque' },
    { key: 'visa', label: 'Visa', logo: visa },
    { key: 'mastercard', label: 'Master Card', logo: mastercard },
    { key: 'amex', label: 'AMEX', logo: amex },
    { key: 'discover', label: 'Discover', logo: discover },
    { key: 'financing', label: 'Financing' },
    { key: 'other', label: 'Other' },
];

/**
 * Optimized styles
 */

const ChipWrapper = styled('div')({
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
});

const StyledChip = styled(Chip)(({ $selected }) => ({
    height: '36px',
    fontFamily: 'Roboto',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    textTransform: 'capitalize',
    paddingLeft: 12,
    paddingRight: 12,
    gap: '0px',

    ...($selected
        ? {
              borderRadius: '50px',
              border: '1px solid #0066E1',
              backgroundColor: '#007AFF',
              boxShadow: '0 2px 2px 0 rgba(0, 122, 255, 0.25)',
              color: '#FFF',
              '&:hover': {
                  backgroundColor: '#007AFF',
                  borderColor: '#0066E1',
                  boxShadow: '0 2px 2px 0 rgba(0, 122, 255, 0.25)',
              },
          }
        : {
              borderRadius: '100px',
              border: '1px solid #DCDCDC',
              backgroundColor: '#FFF',
              boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
              color: '#000',
              '&:hover': {
                  backgroundColor: '#FFF',
                  borderColor: '#DCDCDC',
                  boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
              },
          }),
}));

const styles = {
    dialogHeader: {
        height: '64px',
        padding: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#002394',
        color: 'white',
    },
    dialogContent: {
        padding: '24px !important',
        maxHeight: '726px',
        overflow: 'auto',
    },
    datePickerButton: {
        width: 36,
        height: 36,
        border: '1px solid #ccc',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: '0.2s',
        '&:hover': {
            borderColor: '#1976d2',
        },
    },
    toggleButton: {
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 16px',
        borderRadius: '50px',
        fontSize: '16px',
        fontWeight: 400,
        textTransform: 'none',
        LineHeight: '24px',
        letterSpacing: '0.15px',
        minWidth: '60px',
    },
    paymentSummaryBox: {
        padding: '12px 12px 12px 12px',
        width: '100%',
        borderRadius: '0',
    },
    paymentMethodRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '0 0',
    },
    selectField: {
        minWidth: '150px',
        '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
        },
    },
    amountField: {
        width: '120px',
        '& .MuiInputBase-root': {
            height: '40px',
        },
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        marginBottom: '24px',
    },
    sectionIcon: {
        width: '20px',
        height: '20px',
        backgroundColor: '#1976d2',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '12px',
        fontWeight: 'bold',
    },
    datePickerButton: {
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        border: '1px solid rgba(0, 0, 0, 0.10)!important',
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            borderColor: '#1976d2',
        },
        '&.selected': {
            backgroundColor: '#1976d2',
            borderColor: '#1976d2',
            color: 'white',
        },
    },

    actionCard: {
        padding: '12px',
        borderRadius: '8px',
        border: '1px solid var(--box-stroke-10, rgba(0, 0, 0, 0.10))!important',
        boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
    },

    actionRowPublish: {
        height: 'max-content',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '20px',
    },
    actionRowGoogle: {
        height: 'max-content',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
    },
    actionLeftContent: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    carfaxImg: {
        height: '19px',
        display: 'block',
    },
    googleReviewImg: {
        height: '24px',
        width: '24px',
    },
    invoiceSentChip: {
        height: '40px',
        borderRadius: '36px',
        backgroundColor: '#EDF7ED',
        color: '#2E7D32',
        fontFamily: 'Roboto',
        padding: '2px 12px',
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: '24px',
        letterSpacing: '0.15px',

        '& .MuiChip-icon': {
            color: '#2E7D32',
            fontSize: '20px',
            marginLeft: '0px',
            marginRight: '4px',
        },

        '& .MuiChip-label': {
            // paddingLeft: '4px',
            // paddingRight: '12px',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
        },
    },
};

const StyledToggleButtonGroupLaptop = styled(ToggleButtonGroup)(({ theme }) => ({
    borderRadius: '50px',
    padding: '6px',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: 'fit-content',
    '& .MuiToggleButton-root': {
        border: '1px solid var(--box-stroke-10, rgba(0, 0, 0, 0.10))!important',
        fontFamily: 'Roboto',
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '24px',
        letterSpacing: '0.15px',
        borderRadius: '40px',
        padding: '8px 20px',
        transition: 'none',
        textTransform: 'capitalize',
        height: '40px',
        display: 'flex',
        gap: '6px',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        minWidth: 0,
        '&.Mui-selected': {
            backgroundColor: '#007AFF',
            color: '#fff',
        },
        '&:not(.Mui-selected)': {
            backgroundColor: '#fff',
            color: '#000',
        },
        '&:not(.Mui-selected):hover': {
            backgroundColor: '#fff',
            color: '#000',
        },
        '&.Mui-selected:hover': {
            backgroundColor: '#007AFF',
        },
    },
}));

const RecordPaymentDialog = ({
    allowCarfax = false,
    shopConfigData,
    showInvoiceSentChip = false,
    open,
    googleReviewDetails = {},
    setGoogleReviewDetails,
    appointmentData = null,
    appointmentIdFromUrl,
    googleReviewMessage = '',
    shopName = '',
    invoiceData,
    paymentMethods,
    setPaymentMethods,
    isCarfaxPublished,
    onClose,
    control,
    paymentDetails,
    setPaymentDetails,
    isNewTaxVersion = false,
    taxesBreakdown = [],
    setTaxesBreakdown,
    documentType = DOCUMENT_TAX_TYPE.INVOICE,
    setIsRecordModal,
    taxableLines = null,
    hideEmailBtn = false,
    saveEmailFn,
    saveFn,
}) => {
    const theme = useTheme();
    const queryClient = useQueryClient();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const { data: shopTaxesData } = useGetShopTaxes();

    const showMultiTax = isNewTaxVersion || checkIsNewTaxVersion(invoiceData);

    const activeBreakdown = useMemo(() => {
        const raw = taxesBreakdown?.length > 0 ? taxesBreakdown : showMultiTax && invoiceData?.taxesBreakdown?.length > 0 ? invoiceData.taxesBreakdown : [];
        return normalizeTaxesBreakdownFromResponse(raw, documentType);
    }, [taxesBreakdown, showMultiTax, invoiceData?.taxesBreakdown, documentType]);

    const componentTotals = useMemo(() => {
        const hasPaymentComponents =
            paymentDetails?.labourTotal != null &&
            paymentDetails?.partsTotal != null &&
            !Number.isNaN(Number(paymentDetails.labourTotal)) &&
            !Number.isNaN(Number(paymentDetails.partsTotal));

        if (hasPaymentComponents) {
            return {
                labourTotal: Number(paymentDetails.labourTotal) || 0,
                partsTotal: Number(paymentDetails.partsTotal) || 0,
                packagesTotal: Number(paymentDetails.packagesTotal) || 0,
            };
        }

        if (Array.isArray(invoiceData?.services) && invoiceData.services.length > 0) {
            return computeLabourPartsFromServices(invoiceData.services);
        }

        return { labourTotal: 0, partsTotal: 0, packagesTotal: 0 };
    }, [paymentDetails?.labourTotal, paymentDetails?.partsTotal, paymentDetails?.packagesTotal, invoiceData?.services]);

    // Taxable lines for the per-line tax engine: create flow passes them live, detail flow derives
    // them from the saved record. When present, tax is computed exactly as on the invoice page
    // (round each line's tax, then sum) so opening this dialog can never shift the total.
    const linesForTax = useMemo(() => {
        if (Array.isArray(taxableLines) && taxableLines.length > 0) return taxableLines;
        if (invoiceData?.services?.length > 0) return buildTaxableLinesFromRecord(invoiceData);
        return null;
    }, [taxableLines, invoiceData]);

    // Per-tax summary for the payment-summary checkbox — same computation as the invoice editor, so
    // the "edited from individual lines" lock (customized) behaves identically here (and on detail
    // pages, which open this same dialog).
    const lineTaxSummary = useMemo(() => {
        if (!showMultiTax || !linesForTax || !activeBreakdown.length) return [];
        const merged = mergeShopTaxRulesIntoBreakdown(activeBreakdown, getShopTaxRules(shopTaxesData));
        return summarizeTaxRows({
            lines: linesForTax,
            taxesBreakdown: merged,
            discount: paymentDetails?.discount ?? 0,
            subTotal: paymentDetails?.subTotal ?? 0,
            documentType,
        });
    }, [showMultiTax, linesForTax, activeBreakdown, shopTaxesData, paymentDetails?.discount, paymentDetails?.subTotal, documentType]);

    const taxesBreakdownKey = JSON.stringify(
        activeBreakdown.map((r) => ({
            taxId: r.taxId,
            applyTax: r.applyTax,
            invoiceRate: r.invoiceRate,
            estimateRate: r.estimateRate,
        }))
    );

    // Hydrate breakdown when modal opens (parent state or invoice GET)
    useEffect(() => {
        if (!open || !showMultiTax || !setTaxesBreakdown) return;
        if (taxesBreakdown?.length > 0) return;

        if (invoiceData?.taxesBreakdown?.length > 0) {
            setTaxesBreakdown(invoiceData.taxesBreakdown);
            return;
        }

        if (!invoiceData?._id) {
            const built = resolveBreakdownForCalc({
                isNewTaxVersion: true,
                id: null,
                taxesBreakdown: [],
                shopTaxesData,
                documentType,
            });
            if (built.length > 0) {
                setTaxesBreakdown(built);
            }
        }
    }, [open, showMultiTax, setTaxesBreakdown, taxesBreakdown?.length, invoiceData, shopTaxesData, documentType]);

    // Recalculate tax/total when multi-tax rows or payment bases change
    useEffect(() => {
        if (!open || !showMultiTax || !activeBreakdown.length) return;

        const mergedBreakdown = mergeShopTaxRulesIntoBreakdown(activeBreakdown, getShopTaxRules(shopTaxesData));
        // Prefer the per-line engine (same one the invoice page uses). Fall back to the aggregated
        // calc only when no taxable lines are available.
        const result = linesForTax
            ? calculateMultiTaxFromLines({
                  taxesBreakdown: mergedBreakdown,
                  lines: linesForTax,
                  discount: paymentDetails?.discount ?? 0,
                  subTotal: paymentDetails?.subTotal ?? 0,
                  documentType,
              })
            : calculateMultiTaxLines({
                  taxesBreakdown: mergedBreakdown,
                  labourTotal: componentTotals.labourTotal,
                  partsTotal: componentTotals.partsTotal,
                  packagesTotal: componentTotals.packagesTotal,
                  fees: paymentDetails?.fees ?? 0,
                  discount: paymentDetails?.discount ?? 0,
                  subTotal: paymentDetails?.subTotal ?? 0,
                  documentType,
              });

        const subTotal = Number(paymentDetails?.subTotal) || 0;
        const fees = Number(paymentDetails?.fees) || 0;
        const discount = Number(paymentDetails?.discount) || 0;
        const total = subTotal + fees - discount + (Number(result.tax) || 0);
        setPaymentDetails((prev) => ({
            ...prev,
            tax: result.tax,
            taxPercentage: result.taxPercentage,
            total,
        }));

        // Sync the computed per-tax rows so the summary displays the right amounts. This is correct
        // for every case now that buildTaxableLinesFromRecord applies the Florida labour-only rule
        // (see linesForTax) — the recompute no longer over-taxes FL labour-only, so this no longer
        // needs to be disabled to work around it.
        if (setTaxesBreakdown && shouldSyncCalculatedBreakdown(result.taxesBreakdown)) {
            setTaxesBreakdown((prev) => (JSON.stringify(prev) === JSON.stringify(result.taxesBreakdown) ? prev : result.taxesBreakdown));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        open,
        showMultiTax,
        taxesBreakdownKey,
        shopTaxesData,
        documentType,
        paymentDetails?.subTotal,
        paymentDetails?.fees,
        paymentDetails?.discount,
        componentTotals.labourTotal,
        componentTotals.partsTotal,
        componentTotals.packagesTotal,
        linesForTax,
    ]);

    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedPaymentToEdit, setSelectedPaymentToEdit] = useState(null);
    const [selectedPaymentIndex, setSelectedPaymentIndex] = useState(null);
    const [paymentMethodList, setPaymentMethodList] = useState([]);
    const [submitSave, setSubmitSave] = useState({
        save: false,
        type: null,
    });

    const [linkedAppointmentOpen, setLinkedAppointmentOpen] = useState(false);
    const [pendingSaveAction, setPendingSaveAction] = useState(null);
    const [selectedLinkAppointmentId, setSelectedLinkAppointmentId] = useState(null);

    const updateStatusMutation = useUpdateAppointmentStatus();

    const isSingle = appointmentData != null && !Array.isArray(appointmentData);
    const isArray = Array.isArray(appointmentData) && appointmentData.length >= 1;
    const showMarkComplete = isSingle && appointmentIdFromUrl && appointmentData.status !== APPOINTMENT_STATUS.CUSTOMER_PAID;
    const showLink = (isSingle && !appointmentIdFromUrl) || isArray;
    const isAppointmentAlreadyCompleted = isSingle && !appointmentIdFromUrl && appointmentData?.status === APPOINTMENT_STATUS.CUSTOMER_PAID;
    // Derive channel selection from googleReviewDetails (synced with parent)
    const selectedChannels = useMemo(() => {
        const ch = [];
        if (googleReviewDetails.shouldSendReviewSMSToCustomer) ch.push('text');
        if (googleReviewDetails.shouldSendReviewEmailToCustomer) ch.push('email');
        if (googleReviewDetails.shouldSendReviewNotificationToCustomer) ch.push('app');
        return ch;
    }, [
        googleReviewDetails.shouldSendReviewSMSToCustomer,
        googleReviewDetails.shouldSendReviewEmailToCustomer,
        googleReviewDetails.shouldSendReviewNotificationToCustomer,
    ]);

    console.log('selectedChannels', selectedChannels);
    const getDynamicPlaceholder = (shopData) => {
        const customMessage = shopData?.shopThankYouEmailCustomMessage || DEFAULT_EMAIL_MESSAGES.OPENING(shopName);

        const closingMessage = shopData?.shopThankYouEmailClosingMessage || DEFAULT_EMAIL_MESSAGES.CLOSING;

        return `${customMessage}${customMessage && closingMessage ? '\n\n' : ''}${closingMessage}`.trim();
    };

    useEffect(() => {
        if (open) {
            setPaymentMethodList(structuredClone(paymentMethods));
        }
    }, [open]);

    // Separate existing and new payment methods
    const existingPayments = useMemo(() => {
        return paymentMethodList.filter((method) => method._id && !method.deleted);
    }, [paymentMethodList]);

    const newPayments = useMemo(() => {
        return paymentMethodList.filter((method) => !method._id);
    }, [paymentMethodList]);

    // Calculate amounts using NumericEngine for consistent rounding
    const previousAmountPaid = useMemo(() => {
        return Number(existingPayments.reduce((acc, m) => NumericEngine.add(acc, String(Number(m.amount) || 0)), '0'));
    }, [existingPayments]);

    const currentPayment = useMemo(() => {
        return Number(newPayments.reduce((acc, m) => NumericEngine.add(acc, String(Number(m.amount) || 0)), '0'));
    }, [newPayments]);

    const totalAmountPaid = useMemo(() => {
        return Number(NumericEngine.add(String(previousAmountPaid), String(currentPayment)));
    }, [previousAmountPaid, currentPayment]);

    // If paymentDetails.total might be a string, coerce to number first
    const totalAmount = Number(paymentDetails?.total) || 0;

    const amountDue = useMemo(() => {
        const result = NumericEngine.subtract(String(totalAmount), String(totalAmountPaid));
        return Math.max(0, Number(result));
    }, [totalAmount, totalAmountPaid]);

    const [showPaymentSummary, setShowPaymentSummary] = useState(false);

    const handleAddPaymentMethod = () => {
        const newMethod = {
            transactionType: 'payment',
            paymentMethod: 'credit',
            amount: '',
            paymentDate: null,
            paymentReference: '',
        };
        setPaymentMethodList([...paymentMethodList, newMethod]);
    };

    const handleRemovePaymentMethod = (index) => {
        // Only allow removal of new payments (without _id)
        const methodToRemove = paymentMethodList[index];
        if (!methodToRemove._id && paymentMethodList.filter((m) => !m._id).length > 1) {
            setPaymentMethodList(paymentMethodList.filter((method, i) => i !== index));
        }
    };

    const handlePaymentMethodChange = (index, field, value) => {
        // Only allow editing of new payments (without _id)
        const methodToEdit = paymentMethodList[index];
        if (!methodToEdit._id) {
            setPaymentMethodList(paymentMethodList.map((method, i) => (i === index ? { ...method, [field]: value } : method)));
        }
    };

    const handleAmountChange = (index, value) => {
        // Only allow editing of new payments (without _id)
        const methodToEdit = paymentMethodList[index];
        if (!methodToEdit._id) {
            // Remove non-numeric characters except decimal point
            const numericValue = value.replace(/[^\d.]/g, '');
            if (numericValue === '' || /^\d*(\.\d{0,2})?$/.test(numericValue)) {
                handlePaymentMethodChange(index, 'amount', numericValue);
            }
        }
    };

    const handleDateChange = (index, date) => {
        const methodToEdit = paymentMethodList[index];
        if (!methodToEdit._id) {
            handlePaymentMethodChange(index, 'paymentDate', date);
        }
    };

    const handleReferenceChange = (index, value) => {
        const methodToEdit = paymentMethodList[index];
        if (!methodToEdit._id) {
            handlePaymentMethodChange(index, 'paymentReference', value);
        }
    };

    const handleClose = () => {
        setSubmitSave({ save: false, type: null });
        setLinkedAppointmentOpen(false);
        setPendingSaveAction(null);
        setSelectedLinkAppointmentId(null);
        onClose();
    };

    const handleLinkedDialogSkip = () => {
        setLinkedAppointmentOpen(false);
        if (pendingSaveAction === 'email') {
            saveEmailFn?.();
        } else if (pendingSaveAction === 'save') {
            saveFn?.();
        }
        setPendingSaveAction(null);
    };

    const handleLinkedDialogMarkComplete = async () => {
        const singleAppointment = isSingle ? appointmentData : null;
        if (!singleAppointment?._id) return;

        let queryString = '';
        const shouldSendReviewEmailToCustomer = false;
        const shouldSendReviewSMSToCustomer = false;
        const shouldSendReviewNotificationToCustomer = false;
        queryString = new URLSearchParams({
            shouldSendReviewEmailToCustomer,
            shouldSendReviewSMSToCustomer,
            shouldSendReviewNotificationToCustomer,
        }).toString();

        const payload = { status: APPOINTMENT_STATUS.CUSTOMER_PAID };
        const fullUrl = queryString ? `${singleAppointment._id}?${queryString}` : singleAppointment._id;
        try {
            await updateStatusMutation.mutateAsync({ id: fullUrl, payload });
            queryClient.invalidateQueries({ queryKey: appointmentKeys.all, refetchType: 'all' });
            setLinkedAppointmentOpen(false);
            if (pendingSaveAction === 'email') {
                saveEmailFn?.();
            } else if (pendingSaveAction === 'save') {
                saveFn?.();
            }
            setPendingSaveAction(null);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to update appointment status');
        }
    };

    const getSelectedIdForLinkFlow = () => {
        if (isSingle) return appointmentData._id;
        if (isArray && appointmentData.length === 1) return appointmentData[0]._id;
        return selectedLinkAppointmentId ?? null;
    };

    const handleLinkSkip = () => {
        setLinkedAppointmentOpen(false);
        setSelectedLinkAppointmentId(null);
        if (pendingSaveAction === 'email') {
            saveEmailFn?.();
        } else if (pendingSaveAction === 'save') {
            saveFn?.();
        }
        setPendingSaveAction(null);
    };

    const handleLinkOnly = () => {
        const selectedId = getSelectedIdForLinkFlow();
        if (isArray && appointmentData.length > 1 && !selectedId) return;
        setLinkedAppointmentOpen(false);
        setSelectedLinkAppointmentId(null);
        if (pendingSaveAction === 'email') {
            saveEmailFn?.({ appointmentId: selectedId ?? undefined });
        } else if (pendingSaveAction === 'save') {
            saveFn?.({ appointmentId: selectedId ?? undefined });
        }
        setPendingSaveAction(null);
    };

    const handleLinkAndComplete = async () => {
        const selectedId = getSelectedIdForLinkFlow();
        if (isArray && appointmentData.length > 1 && !selectedId) return;
        const payload = { status: APPOINTMENT_STATUS.CUSTOMER_PAID };
        const queryString = new URLSearchParams({
            shouldSendReviewEmailToCustomer: false,
            shouldSendReviewSMSToCustomer: false,
            shouldSendReviewNotificationToCustomer: false,
        }).toString();
        const fullUrl = queryString ? `${selectedId}?${queryString}` : selectedId;
        try {
            await updateStatusMutation.mutateAsync({ id: fullUrl, payload });
            queryClient.invalidateQueries({ queryKey: appointmentKeys.all, refetchType: 'all' });
            setLinkedAppointmentOpen(false);
            setSelectedLinkAppointmentId(null);
            if (pendingSaveAction === 'email') {
                saveEmailFn?.({ appointmentId: selectedId ?? undefined });
            } else if (pendingSaveAction === 'save') {
                saveFn?.({ appointmentId: selectedId ?? undefined });
            }
            setPendingSaveAction(null);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to update appointment status');
        }
    };

    const renderPaymentMethodOption = (method) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {method.logo && <img src={method.logo} alt={method.label} style={{ height: '20px', width: 'auto' }} />}
            <Typography>{method.label}</Typography>
        </Box>
    );

    // Format date for display
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    // Get payment method label
    const getPaymentMethodLabel = (key) => {
        const method = PAYMENT_METHODS.find((m) => m.key === key);
        return method ? method.label : key;
    };

    const handleEditPayment = (payment, index) => {
        setSelectedPaymentToEdit(payment);
        setSelectedPaymentIndex(index);
        setEditDialogOpen(true);
    };

    // Add this function to handle saving the edited payment
    const handleSaveEditedPayment = (updatedPayment) => {
        if (selectedPaymentIndex !== null && selectedPaymentToEdit) {
            // Update the payment methods array
            const updatedPaymentMethods = paymentMethodList.map((method, index) => {
                if (method._id === selectedPaymentToEdit._id) {
                    return {
                        ...method,
                        paymentMethod: updatedPayment.paymentMethod,
                        amount: updatedPayment.amount,
                        paymentDate: updatedPayment.paymentDate,
                        paymentReference: updatedPayment.paymentReference,
                    };
                }
                return method;
            });

            setPaymentMethodList(updatedPaymentMethods);
            setEditDialogOpen(false);
            setSelectedPaymentToEdit(null);
            setSelectedPaymentIndex(null);
        }
    };

    const handleSaveDeletePayment = () => {
        if (selectedPaymentIndex !== null && selectedPaymentToEdit) {
            const updatedPaymentMethods = paymentMethodList.filter((method, index) => index !== selectedPaymentIndex);
            setPaymentMethodList(updatedPaymentMethods);
            setEditDialogOpen(false);
            setSelectedPaymentToEdit(null);
            setSelectedPaymentIndex(null);
        }
    };

    const DatePickerButton = ({ method, actualIndex, handleDateChange }) => {
        const [anchorEl, setAnchorEl] = useState(null);
        const hasDate = method?.paymentDate && dayjs(method.paymentDate).isValid();
        const dateDisplay = hasDate ? dayjs(method.paymentDate).format('MMMM D, YYYY') : '';

        const handleOpen = (event) => {
            setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
            setAnchorEl(null);
        };

        const open = Boolean(anchorEl);

        return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <HtmlTooltipSecondary title={hasDate ? dateDisplay : ''} arrow placement="top">
                    <Box
                        sx={{
                            ...styles.datePickerButton,
                            ...(hasDate && {
                                backgroundColor: '#1976d2',
                                borderColor: '#1976d2',
                                color: 'white',
                            }),
                        }}
                        onClick={handleOpen}
                    >
                        <CalendarTodayIcon sx={{ fontSize: '18px' }} />
                    </Box>
                </HtmlTooltipSecondary>

                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <DateCalendar
                        value={method.paymentDate ? dayjs(method.paymentDate) : null}
                        onChange={(newDate) => {
                            handleDateChange(actualIndex, newDate ? newDate.toDate() : null);
                            handleClose();
                        }}
                    />
                </Popover>
            </LocalizationProvider>
        );
    };

    useEffect(() => {
        if (submitSave.save) {
            if (submitSave.type === 'email') {
                saveEmailFn();
            } else if (submitSave.type === 'save') {
                saveFn();
            }
            setSubmitSave({ save: false, type: null });
        }
    }, [submitSave]);

    const toggleChannel = (channel) => {
        if (!setGoogleReviewDetails) return;
        setGoogleReviewDetails((prev) => {
            const next = { ...prev };
            if (channel === 'text') next.shouldSendReviewSMSToCustomer = !prev.shouldSendReviewSMSToCustomer;
            if (channel === 'email') next.shouldSendReviewEmailToCustomer = !prev.shouldSendReviewEmailToCustomer;
            if (channel === 'app') next.shouldSendReviewNotificationToCustomer = !prev.shouldSendReviewNotificationToCustomer;
            return next;
        });
    };

    const chipIconColor = (selected) => (selected ? '#FFF' : '#000');

    const channels = [
        { id: 'text', label: 'Text', Icon: ReviewRequestTextSvg, size: 18 },
        { id: 'email', label: 'Email', Icon: ReviewRequestEmailSvg, size: 24 },
        { id: 'app', label: 'App', Icon: ReviewRequestAppSvg, size: 24 },
    ];

    return (
        <Dialog
            open={open}
            onClose={(e) => e.stopPropagation()}
            fullScreen={fullScreen}
            maxWidth={false}
            fullWidth
            sx={{
                '& .MuiPaper-root': {
                    borderRadius: '8px',
                    maxWidth: '900px',
                    width: '100%',
                },
            }}
            disableEscapeKeyDown
            disableEnforceFocus
        >
            {/* Header */}
            <DialogTitle sx={styles.dialogHeader}>
                <Typography variant="h6" color="inherit">
                    Do you want to record payment?
                </Typography>
                <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* Content */}
            <DialogContent sx={styles.dialogContent}>
                <Grid container spacing={2}>
                    {/* Left Column - Payment Details */}
                    <Grid item xs={12} md={7}>
                        <Box>
                            {/* Payment Details Header */}
                            <Box sx={styles.sectionHeader}>
                                <RecordPaymentDetailSvg />
                                <Typography variant="h6" fontWeight={500}>
                                    Payment Details
                                </Typography>
                            </Box>

                            <Stack spacing={3}>
                                {/* Payment Methods */}
                                <Box>
                                    <Grid container spacing={1.2} sx={{ mb: 1 }}>
                                        <Grid item xs={newPayments.length > 1 ? 3.8 : 4.2}>
                                            <Typography variant="body1" fontWeight={500}>
                                                Type
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={newPayments.length > 1 ? 3 : 3.3}>
                                            <Typography variant="body1" fontWeight={500}>
                                                Amount
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={newPayments.length > 1 ? 3 : 3.3}>
                                            <Typography variant="body1" fontWeight={500}>
                                                Reference
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={1.2}>
                                            <Typography variant="body1" fontWeight={500}>
                                                {/* Empty for date picker icon */}
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    <Stack width={'100%'} rowGap={2}>
                                        {newPayments.map((method, index) => {
                                            const actualIndex = paymentMethodList.findIndex((m) => m === method);
                                            return (
                                                <Grid container spacing={1} key={method.id || actualIndex} alignItems={'center'}>
                                                    {/* Payment Method Select */}
                                                    <Grid item xs={newPayments.length > 1 ? 3.8 : 4.2}>
                                                        <Select
                                                            fullWidth
                                                            value={method.paymentMethod}
                                                            onChange={(e) => handlePaymentMethodChange(actualIndex, 'paymentMethod', e.target.value)}
                                                            sx={{ ...MuiSelectWithFocus }}
                                                            MenuProps={{ PaperProps: { style: { maxHeight: '190px' } } }}
                                                        >
                                                            {PAYMENT_METHODS.map((option) => (
                                                                <MenuItem key={option.key} value={option.key}>
                                                                    {renderPaymentMethodOption(option)}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </Grid>

                                                    {/* Amount */}
                                                    <Grid item xs={newPayments.length > 1 ? 3 : 3.3}>
                                                        <TextField
                                                            fullWidth
                                                            value={method.amount}
                                                            onChange={(e) => handleAmountChange(actualIndex, e.target.value)}
                                                            onFocus={(e) => {
                                                                if (!method.amount) {
                                                                    handleAmountChange(actualIndex, formatNumberAsWhole(amountDue).toString());
                                                                }
                                                            }}
                                                            size="small"
                                                            sx={MuiTextFieldWithFocus}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <p
                                                                        style={{
                                                                            paddingLeft: '0',
                                                                            fontSize: '16px',
                                                                        }}
                                                                    >
                                                                        $
                                                                    </p>
                                                                ),
                                                            }}
                                                        />
                                                    </Grid>

                                                    {/* Reference */}
                                                    <Grid item xs={newPayments.length > 1 ? 3 : 3.3}>
                                                        <TextField
                                                            fullWidth
                                                            value={method.paymentReference || ''}
                                                            onChange={(e) => handleReferenceChange(actualIndex, e.target.value)}
                                                            placeholder="Reference"
                                                            size="small"
                                                            sx={MuiTextFieldWithFocus}
                                                        />
                                                    </Grid>
                                                    {/* Date Picker */}
                                                    <Grid item xs={1.2}>
                                                        <DatePickerButton
                                                            method={method}
                                                            index={index}
                                                            actualIndex={actualIndex}
                                                            handleDateChange={handleDateChange}
                                                        />
                                                    </Grid>

                                                    {/* Delete Button */}
                                                    {newPayments.length > 1 && (
                                                        <Grid item xs={1} textAlign={'right'}>
                                                            <IconButton onClick={() => handleRemovePaymentMethod(actualIndex)} size="small" color="error">
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            );
                                        })}

                                        <Box display={'flex'} gap={1} alignItems="center">
                                            <AddIcon sx={{ color: '#1976d2', cursor: 'pointer' }} />
                                            <Typography
                                                onClick={handleAddPaymentMethod}
                                                sx={{
                                                    color: '#1976d2',
                                                    textTransform: 'none',
                                                    justifyContent: 'flex-start',
                                                    fontWeight: 500,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                ADD PAYMENT METHOD
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>

                                {/* Internal Memo */}
                                <Box>
                                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 500, color: '#212121' }}>
                                        Shop Internal Memo
                                    </Typography>
                                    <Controller
                                        name="note"
                                        control={control}
                                        render={({ field }) => <TextField {...field} multiline maxRows={5} fullWidth sx={MuiMultiFieldWithFocus} />}
                                    />
                                </Box>
                                {/* Publish to Carfax */}
                                {allowCarfax && (
                                    <Box sx={styles.actionCard}>
                                        <Box sx={styles.actionRowPublish}>
                                            <Box sx={{ ...styles.actionLeftContent, gap: '0px' }}>
                                                <Typography
                                                    sx={{
                                                        color: '#212121',
                                                        fontWeight: 500,
                                                        lineHeight: 'normal',
                                                    }}
                                                >
                                                    Publish invoice to
                                                </Typography>
                                                <img src={carfex} alt="carfax" style={styles.carfaxImg} />

                                                <CustomTooltip
                                                    width="280px"
                                                    placement="top"
                                                    title={
                                                        <Typography fontSize="14px" fontWeight={400} align="center" letterSpacing="-0.42px" lineHeight="140%">
                                                            {isCarfaxPublished
                                                                ? 'Invoice has been permanently published to Carfax'
                                                                : 'Unpublish before 11:59 PM to remove from Carfax'}
                                                        </Typography>
                                                    }
                                                >
                                                    <InfoOutlined sx={{ fontSize: '24px', color: '#8C8C8C', cursor: 'pointer' }} />
                                                </CustomTooltip>
                                            </Box>
                                            {isCarfaxPublished ? (
                                                <Chip icon={<CheckCircleOutlined />} label="Published" sx={styles.invoiceSentChip} />
                                            ) : (
                                                <Switch
                                                    checked={!!googleReviewDetails.shouldSendInvoiceToCarfax}
                                                    onChange={(e) =>
                                                        setGoogleReviewDetails?.((prev) => ({ ...prev, shouldSendInvoiceToCarfax: e.target.checked }))
                                                    }
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                )}

                                {/* Google Review Request */}
                                <Box sx={{ ...styles.actionCard, mt: 2 }}>
                                    <Box sx={styles.actionRowGoogle}>
                                        <Box sx={styles.actionLeftContent}>
                                            {/* <img src={googleImg} alt="google" style={styles.googleReviewImg} /> */}
                                            <GoogleLogoSvg />
                                            <Typography
                                                sx={{
                                                    color: '#212121',
                                                    fontWeight: 500,
                                                    lineHeight: 'normal',
                                                }}
                                            >
                                                Send a Google review request?
                                            </Typography>
                                        </Box>
                                        {showInvoiceSentChip ? (
                                            <Chip icon={<CheckCircleOutlined />} label="Sent" sx={styles.invoiceSentChip} />
                                        ) : (
                                            <Switch
                                                checked={!!googleReviewDetails.sendGoogleReviewRequest}
                                                onChange={(e) => setGoogleReviewDetails?.((prev) => ({ ...prev, sendGoogleReviewRequest: e.target.checked }))}
                                            />
                                        )}
                                    </Box>

                                    {!showInvoiceSentChip && googleReviewDetails.sendGoogleReviewRequest && (
                                        <>
                                            <Divider
                                                sx={{
                                                    marginTop: '14px',
                                                    marginBottom: '20px',
                                                }}
                                            />

                                            <ChipWrapper>
                                                {channels.map(({ id, label, Icon, size }) => {
                                                    const selected = selectedChannels.includes(id);
                                                    const color = chipIconColor(selected);
                                                    return (
                                                        <StyledChip
                                                            key={id}
                                                            $selected={selected}
                                                            onClick={() => toggleChannel(id)}
                                                            icon={<Icon color={color} size={size} />}
                                                            label={label}
                                                            aria-label={`${id} request`}
                                                            clickable
                                                        />
                                                    );
                                                })}
                                            </ChipWrapper>

                                            {googleReviewMessage && (
                                                <Typography
                                                    sx={{
                                                        textAlign: 'center',
                                                        marginTop: '20px',
                                                        fontSize: '14px',
                                                        fontWeight: 400,
                                                        lineHeight: '24px',
                                                        letterSpacing: '0.15px',
                                                        color: 'rgba(0, 0, 0, 0.60)',
                                                    }}
                                                >
                                                    {googleReviewMessage}
                                                </Typography>
                                            )}

                                            {selectedChannels.includes('email') && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        marginTop: '20px',
                                                        gap: '8px',
                                                    }}
                                                >
                                                    <Typography variant="body2" fontWeight={500} fontSize={16} sx={{ letterSpacing: '0.5px' }}>
                                                        Customize email message
                                                    </Typography>

                                                    <TextField
                                                        fullWidth
                                                        multiline
                                                        value={googleReviewDetails.customMessage ?? ''}
                                                        onChange={(e) => setGoogleReviewDetails?.((prev) => ({ ...prev, customMessage: e.target.value }))}
                                                        placeholder={getDynamicPlaceholder(shopConfigData)}
                                                        sx={{
                                                            '& .MuiInputBase-input::placeholder': {
                                                                fontWeight: 400,
                                                                lineHeight: '130%',
                                                                letterSpacing: '0.15px',
                                                                color: '#9E9E9E',
                                                                opacity: 1,
                                                            },
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: '8px',
                                                                padding: '12px!important',
                                                                paddingBottom: googleReviewDetails.customMessage ? '12px!important' : '0px!important',
                                                                '& fieldset': {
                                                                    border: '1px solid rgba(0, 0, 0, 0.10)',
                                                                    boxShadow: '0px 2px 3px 0px rgba(0, 0, 0, 0.06)',
                                                                },
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                            )}
                                        </>
                                    )}
                                </Box>
                            </Stack>
                        </Box>
                    </Grid>

                    {/* Right Column - Payment Breakdown */}
                    <Grid item xs={12} md={5}>
                        <Box>
                            {/* Payment Breakdown Header */}
                            <Box sx={styles.sectionHeader}>
                                <PaymentBreakdownSvg />
                                <Typography variant="h6" fontWeight={500}>
                                    Payment Breakdown
                                </Typography>
                            </Box>

                            {/* Payment Summary - Collapsible */}
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(0, 0, 0, 0.10)',
                                    boxShadow: '0px 2px 3px 0px rgba(0, 0, 0, 0.06)',
                                    mb: 3,
                                }}
                            >
                                <Box
                                    sx={{
                                        justifyContent: 'space-between',
                                        background: '#F8F8F8',
                                        width: '100%',
                                        textTransform: 'none',
                                        color: '#212121',
                                        fontSize: 16,
                                        fontWeight: 600,
                                        padding: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        borderRadius: '8px 8px 0 0',
                                    }}
                                    onClick={() => setShowPaymentSummary(!showPaymentSummary)}
                                >
                                    Payment Summary
                                    <Box
                                        sx={{
                                            ml: 'auto',
                                            width: '22px',
                                            height: '22px',
                                            background: '#007AFF',
                                            borderRadius: '50%!important',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            transform: showPaymentSummary ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s ease',
                                        }}
                                    >
                                        <ExpandMore
                                            fontSize="small"
                                            sx={{
                                                strokeWidth: 2,
                                                fill: '#fff',
                                            }}
                                        />
                                    </Box>
                                </Box>

                                <Collapse
                                    in={showPaymentSummary}
                                    sx={{
                                        width: '100%',
                                        padding: showPaymentSummary ? '8px 12px' : '0 12px',
                                    }}
                                >
                                    <Stack gap={1}>
                                        <Stack direction={'row'} justifyContent={'space-between'}>
                                            <Typography>Labour</Typography>
                                            <Typography>
                                                {paymentDetails?.labourTotal > 0
                                                    ? NumericEngine.format(paymentDetails.labourTotal, {
                                                          trailingZeros: true,
                                                          formatCurrency: true,
                                                      })
                                                    : '$0.00'}
                                            </Typography>
                                        </Stack>
                                        <Stack direction={'row'} justifyContent={'space-between'}>
                                            <Typography>Parts</Typography>
                                            <Typography>
                                                {paymentDetails?.partsTotal > 0
                                                    ? NumericEngine.format(paymentDetails.partsTotal, {
                                                          trailingZeros: true,
                                                          formatCurrency: true,
                                                      })
                                                    : '$0.00'}
                                            </Typography>
                                        </Stack>
                                        <Stack direction={'row'} justifyContent={'space-between'}>
                                            <Typography>Sub Total</Typography>
                                            <Typography>
                                                {paymentDetails?.subTotal > 0
                                                    ? NumericEngine.format(paymentDetails?.subTotal, { trailingZeros: true, formatCurrency: true })
                                                    : '$0.00'}
                                            </Typography>
                                        </Stack>
                                        <Stack direction={'row'} justifyContent={'space-between'}>
                                            <Typography>Fees</Typography>
                                            <Typography>
                                                {paymentDetails?.fees > 0
                                                    ? NumericEngine.format(paymentDetails?.fees, { trailingZeros: true, formatCurrency: true })
                                                    : '$0.00'}
                                            </Typography>
                                        </Stack>
                                        <Stack direction={'row'} justifyContent={'space-between'}>
                                            <Typography>Discount</Typography>
                                            <Typography>
                                                {paymentDetails?.discountPercentage > 0 && `${formatNumberAsWhole(paymentDetails.discountPercentage)}% - `}
                                                {paymentDetails?.discount > 0
                                                    ? NumericEngine.format(paymentDetails?.discount, { trailingZeros: true, formatCurrency: true })
                                                    : '$0.00'}
                                            </Typography>
                                        </Stack>
                                        {showMultiTax ? (
                                            <MultiTaxRows
                                                taxesBreakdown={activeBreakdown}
                                                setTaxesBreakdown={setTaxesBreakdown}
                                                documentType={documentType}
                                                lineTaxSummary={lineTaxSummary}
                                                hideCheckbox
                                            />
                                        ) : (
                                            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                                <Typography>Tax</Typography>
                                                <Typography display={'flex'} alignItems={'center'}>
                                                    <TextField
                                                        fullWidth
                                                        value={paymentDetails?.taxPercentage}
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment
                                                                    style={{
                                                                        marginLeft: '0',
                                                                    }}
                                                                    position="end"
                                                                >
                                                                    <span style={{ color: '#000' }}>%</span>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        error={isNaN(paymentDetails.taxPercentage)}
                                                        helperText={isNaN(paymentDetails.taxPercentage) ? 'Tax is required' : ''}
                                                        onChange={(e) => {
                                                            const taxPercentage = +e.target.value;
                                                            if (!isNaN(taxPercentage) && taxPercentage < 100) {
                                                                const { subTotal, discount } = paymentDetails;
                                                                const feesAmount = paymentDetails?.fees || 0;

                                                                const taxableAmount = NumericEngine.subtract(
                                                                    NumericEngine.add(String(subTotal), String(feesAmount)),
                                                                    String(discount)
                                                                );

                                                                const tax =
                                                                    taxPercentage > 0
                                                                        ? NumericEngine.multiply(
                                                                              NumericEngine.divide(String(taxPercentage), '100'),
                                                                              taxableAmount
                                                                          )
                                                                        : '0';

                                                                const total = NumericEngine.add(taxableAmount, tax);

                                                                setPaymentDetails({
                                                                    ...paymentDetails,
                                                                    taxPercentage: taxPercentage,
                                                                    tax: Number(tax),
                                                                    total: Number(total),
                                                                });
                                                            } else {
                                                                toast.error('Please enter a valid tax percentage (0-99)');
                                                            }
                                                        }}
                                                        sx={{
                                                            height: '36px',
                                                            width: '85px',
                                                            '& .MuiInputBase-root': {
                                                                height: '36px',
                                                                width: '85px',
                                                                borderRadius: '8px',
                                                                padding: '0 9.5px 0 0',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                width: '85px',
                                                                padding: '6px 9.5px',
                                                                paddingRight: '0',
                                                            },
                                                        }}
                                                    />
                                                    <span style={{ margin: '0 8px' }}>-</span>
                                                    {paymentDetails?.tax > 0
                                                        ? NumericEngine.format(paymentDetails?.tax, { trailingZeros: true, formatCurrency: true })
                                                        : '$0.00'}
                                                </Typography>
                                            </Stack>
                                        )}

                                        <Divider />

                                        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                            <Typography fontWeight={500}>Total</Typography>
                                            <Typography fontWeight={500}>
                                                {paymentDetails?.total > 0
                                                    ? NumericEngine.format(paymentDetails?.total, { trailingZeros: true, formatCurrency: true })
                                                    : '$0.00'}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Collapse>
                            </div>

                            {/* Payment Summary Box */}
                            <div
                                style={{
                                    marginTop: '12px',
                                    paddingBottom: '12px',
                                    width: '100%',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(0, 0, 0, 0.10)',
                                    boxShadow: '0px 2px 3px 0px rgba(0, 0, 0, 0.06)',
                                }}
                            >
                                <Stack spacing={2}>
                                    {!showPaymentSummary && (
                                        <Stack direction="row" justifyContent="space-between" padding={'12px 12px 0 12px'}>
                                            <Typography variant="h6" fontSize={18} fontWeight={600}>
                                                Total
                                            </Typography>
                                            <Typography variant="h6" fontSize={18} fontWeight={600}>
                                                {paymentDetails?.total > 0
                                                    ? NumericEngine.format(paymentDetails?.total, { trailingZeros: true, formatCurrency: true })
                                                    : '$0.00'}
                                            </Typography>
                                        </Stack>
                                    )}

                                    {/* Payment History Table */}
                                    {existingPayments.length > 0 && (
                                        <div style={{ borderRadius: showPaymentSummary ? '8px 8px 0 0 ' : '0' }}>
                                            <Grid
                                                container
                                                sx={{ m: '0', mb: 1, background: '#F8F8F8', width: '100%', padding: '8px 12px', height: '48px' }}
                                                alignItems={'center'}
                                            >
                                                <Grid item xs={4} sx={{ width: '140px' }}>
                                                    <Typography fontSize={16} fontWeight={500}>
                                                        Date
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs sx={{ width: 'auto' }}>
                                                    <Typography fontSize={16} fontWeight={500}>
                                                        Type
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs sx={{ width: 'auto' }} textAlign="right">
                                                    <Typography fontSize={16} fontWeight={500}>
                                                        Amount
                                                    </Typography>
                                                </Grid>
                                            </Grid>

                                            {existingPayments.map((payment, index) => {
                                                const method = PAYMENT_METHODS.find((m) => m.key === payment.paymentMethod);
                                                return (
                                                    <Grid container sx={{ m: '0', width: '100%', padding: '0 12px', mb: 1 }} key={payment._id}>
                                                        <Grid item xs={4}>
                                                            <Typography fontSize={14} fontWeight={400}>
                                                                {formatDate(payment.paymentDate)}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={4} textAlign="flex-start">
                                                            <Typography fontSize={14} fontWeight={400}>
                                                                {method?.logo ? (
                                                                    <>
                                                                        <img
                                                                            src={method.logo}
                                                                            alt={method.label}
                                                                            style={{ width: 'auto', height: '20px', verticalAlign: 'middle', marginRight: 4 }}
                                                                        />
                                                                        {method.label}
                                                                    </>
                                                                ) : (
                                                                    method?.label || payment.paymentMethod
                                                                )}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={4} textAlign="right">
                                                            <Stack direction={'row'} justifyContent={'flex-end'} alignItems={'center'} gap={'4px'}>
                                                                <Edit
                                                                    size={14}
                                                                    style={{
                                                                        color: '#646466',
                                                                        width: '20px',
                                                                        height: '20px',
                                                                        cursor: 'pointer',
                                                                        marginRight: '4px',
                                                                    }}
                                                                    onClick={() => handleEditPayment(payment, index)}
                                                                />
                                                                <Typography fontSize={16} fontWeight={400}>
                                                                    {NumericEngine.format(payment.amount ?? 0, { trailingZeros: true, formatCurrency: true })}
                                                                </Typography>
                                                            </Stack>
                                                        </Grid>
                                                    </Grid>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {existingPayments.length > 0 && (
                                        <div style={{ padding: '0 12px' }}>
                                            <Divider />
                                        </div>
                                    )}

                                    <Stack direction="row" justifyContent="space-between" padding={'12px 12px 0 12px '} sx={{ marginTop: '0px!important' }}>
                                        <Typography fontSize={16} fontWeight={500}>
                                            Previous amount paid
                                        </Typography>
                                        <Typography fontSize={16} fontWeight={500}>
                                            {previousAmountPaid > 0
                                                ? NumericEngine.format(previousAmountPaid, {
                                                      trailingZeros: true,
                                                      formatCurrency: true,
                                                  })
                                                : '$0.00'}
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" justifyContent="space-between" padding={'0 12px'} sx={{ marginTop: '8px!important' }}>
                                        <Typography fontSize={16} color="#2E7D32" fontWeight={500}>
                                            Current Payment
                                        </Typography>
                                        <Typography fontSize={16} color="#2E7D32" fontWeight={500}>
                                            {currentPayment > 0
                                                ? NumericEngine.format(currentPayment, {
                                                      trailingZeros: true,
                                                      formatCurrency: true,
                                                  })
                                                : '$0.00'}
                                        </Typography>
                                    </Stack>
                                    <div style={{ padding: '0 12px' }}>
                                        <Divider />
                                    </div>

                                    <Stack direction="row" justifyContent="space-between" padding={'0 12px'} sx={{ marginTop: '8px!important' }}>
                                        <Typography fontSize={18} fontWeight={600} color="#D32F2F">
                                            Amount Due
                                        </Typography>
                                        <Typography fontSize={18} fontWeight={600} color="#D32F2F">
                                            {amountDue > 0
                                                ? NumericEngine.format(amountDue, {
                                                      trailingZeros: true,
                                                      formatCurrency: true,
                                                  })
                                                : '$0.00'}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </div>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            <EditRecordPaymentDialog
                open={editDialogOpen}
                onClose={() => {
                    setEditDialogOpen(false);
                    setSelectedPaymentToEdit(null);
                    setSelectedPaymentIndex(null);
                }}
                payment={selectedPaymentToEdit}
                index={selectedPaymentIndex}
                onSave={handleSaveEditedPayment}
                onDelete={handleSaveDeletePayment}
                DatePickerButton={DatePickerButton}
            />

            {linkedAppointmentOpen && showMarkComplete && (
                <LinkedAppointmentDialog
                    open={linkedAppointmentOpen}
                    onClose={() => {
                        setLinkedAppointmentOpen(false);
                        setPendingSaveAction(null);
                    }}
                    isAlreadyLinked
                    appointmentDetail={{
                        customerName: appointmentData.customerName ?? '—',
                        appointmentId: appointmentData.appointmentId ?? '—',
                        vehicle: appointmentData.vehicle ?? '—',
                        dateTime: appointmentData.dateTime ?? '—',
                        servicesCount: appointmentData.servicesCount ?? '0 Services',
                        status: (appointmentData.status && APPOINTMENT_STATUS_LABEL[appointmentData.status]) ?? appointmentData.status ?? '—',
                    }}
                    onSkip={handleLinkedDialogSkip}
                    onMarkComplete={handleLinkedDialogMarkComplete}
                    markCompleteLoading={updateStatusMutation.isPending}
                />
            )}
            {linkedAppointmentOpen && showLink && !showMarkComplete && (
                <LinkedAppointmentDialog
                    open={linkedAppointmentOpen}
                    onClose={() => {
                        setLinkedAppointmentOpen(false);
                        setSelectedLinkAppointmentId(null);
                        setPendingSaveAction(null);
                    }}
                    isAlreadyLinked={false}
                    appointmentDetail={
                        isSingle
                            ? {
                                  customerName: appointmentData.customerName ?? '—',
                                  appointmentId: appointmentData.appointmentId ?? '—',
                                  vehicle: appointmentData.vehicle ?? '—',
                                  dateTime: appointmentData.dateTime ?? '—',
                                  servicesCount: appointmentData.servicesCount ?? '0 Services',
                                  status: (appointmentData.status && APPOINTMENT_STATUS_LABEL[appointmentData.status]) ?? appointmentData.status ?? '—',
                              }
                            : isArray && appointmentData.length === 1
                              ? {
                                    customerName: appointmentData[0].customerName ?? '—',
                                    appointmentId: appointmentData[0].appointmentId ?? '—',
                                    vehicle: appointmentData[0].vehicle ?? '—',
                                    dateTime: appointmentData[0].dateTime ?? '—',
                                    servicesCount: appointmentData[0].servicesCount ?? '0 Services',
                                    status:
                                        (appointmentData[0].status && APPOINTMENT_STATUS_LABEL[appointmentData[0].status]) ?? appointmentData[0].status ?? '—',
                                }
                              : undefined
                    }
                    appointmentsList={
                        isArray && appointmentData.length > 1
                            ? appointmentData.map((a) => ({
                                  ...a,
                                  status: (a.status && APPOINTMENT_STATUS_LABEL[a.status]) ?? a.status ?? '—',
                              }))
                            : undefined
                    }
                    selectedAppointmentId={selectedLinkAppointmentId}
                    onSelectAppointment={setSelectedLinkAppointmentId}
                    onSkip={handleLinkSkip}
                    onLinkOnly={handleLinkOnly}
                    onLinkAndComplete={handleLinkAndComplete}
                    linkAndCompleteLoading={updateStatusMutation.isPending}
                    linkButtonsDisabled={isArray && appointmentData.length > 1 && !selectedLinkAppointmentId}
                    hideCompleteButton={isAppointmentAlreadyCompleted}
                />
            )}

            {/* Actions */}
            <DialogActions
                sx={{
                    padding: '16px',
                    borderTop: '1px solid #0000001F',
                    justifyContent: 'space-between',
                }}
            >
                <Button variant="outlined" color="primary" className="btn-text-size-manage" onClick={handleClose}>
                    BACK
                </Button>

                <Stack direction="row" spacing={1}>
                    {!hideEmailBtn && (
                        <Button
                            variant="contained"
                            className="btn-text-size-manage"
                            type="submit"
                            startIcon={<SaveAndSendSvg />}
                            onClick={() => {
                                setPaymentMethods(paymentMethodList);
                                if (showMarkComplete) {
                                    setPendingSaveAction('email');
                                    setLinkedAppointmentOpen(true);
                                } else if (showLink) {
                                    setPendingSaveAction('email');
                                    setSelectedLinkAppointmentId(null);
                                    setLinkedAppointmentOpen(true);
                                } else {
                                    setSubmitSave({ save: true, type: 'email' });
                                }
                            }}
                        >
                            SAVE AND EMAIL
                        </Button>
                    )}

                    <Button
                        variant="contained"
                        color="secondary"
                        className="btn-text-size-manage"
                        onClick={() => {
                            setPaymentMethods(paymentMethodList);
                            if (showMarkComplete) {
                                setPendingSaveAction('save');
                                setLinkedAppointmentOpen(true);
                            } else if (showLink) {
                                setPendingSaveAction('save');
                                setSelectedLinkAppointmentId(null);
                                setLinkedAppointmentOpen(true);
                            } else {
                                setSubmitSave({ save: true, type: 'save' });
                            }
                        }}
                    >
                        SAVE INVOICE
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default RecordPaymentDialog;
